import React, { useState, useEffect } from "react";
import {
  Modal,
  Steps,
  Button,
  Space,
  Typography,
  Card,
  Row,
  Col,
  message,
  Radio,
  Form,
  Input,
  Upload,
  Progress,
  Switch,
  Select,
  Tooltip,
  Popover,
  Spin,
  Popconfirm,
  Badge,
} from "antd";
import {
  UploadOutlined,
  UsergroupAddOutlined,
  RocketOutlined,
  DeleteOutlined,
  SortAscendingOutlined,
  SortDescendingOutlined,
  CheckCircleOutlined,
  FileTextOutlined,
  SettingOutlined,
  ThunderboltOutlined,
  CloseOutlined,
  ArrowRightOutlined,
  SaveOutlined,
} from "@ant-design/icons";
import Papa from "papaparse";
import PaginationTable from "../../../shared/PaginationTable";
import TemplateCSVDownloader from "../../../shared/TemplateCSVDownloader";
import { HashedEmailConfig } from "../../OrgConfig/configs/emailConfig";
import { PhoneNumberConfig } from "../../OrgConfig/configs/phoneNumberConfig";
import {
  encryptStringSHA512,
  encryptStringSHA256,
  encryptStringSHA3,
  encryptStringSHA1,
  encryptStringMD5,
} from "../../../../core/utils/encryptString";

const { Step } = Steps;
const { Title, Text } = Typography;
const { Dragger } = Upload;

// Helper function to map DSP names based on providersVisible permission
const getDSPDisplayName = (dspName, providersVisible) => {
  if (providersVisible) {
    return dspName; // Show actual DSP name
  }
  
  // Map to tier names when providersVisible is false
  switch (dspName?.toLowerCase()) {
    case 'liquidm':
      return 'Tier 1';
    case 'nexxen':
      return 'Tier 2';
    default:
      return dspName; // Return original if not mapped
  }
};

const GuidedWorkflowSteps = ({
  visible,
  onClose,
  createHashedEmailList,
  bulkCreateHashedEmailAddress,
  activateHashedEmailList,
  processDeviceIds,
  refetchHashedEmailLists,
  updateHashedEmailList,
  currentOrg,
  providersVisible,
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [form] = Form.useForm();
  
  // Step 1 - Upload Data states
  const [emails, setEmails] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isUploadComplete, setUploadComplete] = useState(false);
  const [progress, setProgress] = useState(0);
  const [savedHashedEmailCount, setHashedEmailCount] = useState(0);
  const [emailsAlreadyHashed, setEmailsAlreadyHashed] = useState(false);
  const [selectedHashingMethod, setSelectedHashingMethod] = useState("SHA256");
  const [sortOrder, setSortOrder] = useState("ascending");
  const [createdEmailListId, setCreatedEmailListId] = useState(null);
  const [selectedDataType, setSelectedDataType] = useState("emails");
  
  // Step 2 - Map to Device IDs states
  const [isMappingDevices, setIsMappingDevices] = useState(false);
  const [isMappingComplete, setIsMappingComplete] = useState(false);
  
  // DSP provider selection for mapping step
  const [selectedDspProvider, setSelectedDspProvider] = useState("liquidm");
  
  // Auto-switch to Nexxen if phones are selected since LiquidM doesn't support phone numbers
  useEffect(() => {
    if (selectedDataType === "phones" && selectedDspProvider === "liquidm") {
      setSelectedDspProvider("nexxen");
    }
  }, [selectedDataType, selectedDspProvider]);

  const emailRegex = /\S+@\S+\.\S+/;
  const phoneRegex = /^(\+?\d{1,3}[- ]?)?(\(?\d{1,4}\)?[- ]?)?[\d\- ]{7,15}$/;

  const steps = [
    {
      title: selectedDataType === "emails" ? "Upload Emails" : "Upload Phone Numbers",
      icon: <UploadOutlined />,
      description: selectedDataType === "emails" 
        ? "Your email list has been successfully uploaded and is ready for processing"
        : "Your phone number list has been successfully uploaded and is ready for processing",
    },
    {
      title: selectedDataType === "emails" ? "Convert Emails to Device IDs" : "Convert Phone Numbers to Device IDs", 
      icon: <UsergroupAddOutlined />,
      description: selectedDataType === "emails"
        ? "Converting email addresses into device identifiers through secure hashing and identity matching"
        : "Converting phone numbers into device identifiers through secure hashing and identity matching",
    },
  ];

  // Function to detect if data is already hashed
  const detectHashedEmails = (dataArray) => {
    if (dataArray.length === 0) return false;
    
    const hexRegex = /^[0-9a-fA-F]+$/;
    let hashedCount = 0;
    let originalFormatCount = 0;
    
    dataArray.forEach((item) => {
      const trimmedItem = item.trim();
      if (trimmedItem) {
        const isValidLength = (trimmedItem.length === 32 || trimmedItem.length === 40 || trimmedItem.length === 64 || trimmedItem.length === 128);
        const isHexFormat = hexRegex.test(trimmedItem);
        const isHash = isValidLength && isHexFormat;
        
        const isOriginalFormat = selectedDataType === "emails" ? emailRegex.test(trimmedItem) : phoneRegex.test(trimmedItem);
        
        if (isHash) {
          hashedCount++;
        } else if (isOriginalFormat) {
          originalFormatCount++;
        }
      }
    });
    
    // If more than 70% of entries look like hashes, assume they're hashed
    const totalEntries = hashedCount + originalFormatCount;
    if (totalEntries > 0) {
      const hashPercentage = hashedCount / totalEntries;
      return hashPercentage > 0.7;
    }
    
    return false;
  };

  const sortIcon = sortOrder === "ascending" ? <SortAscendingOutlined /> : <SortDescendingOutlined />;

  // Email validation and processing functions
  const removeDuplicates = arr => Array.from(new Set(arr));

  const convertToLowercase = arr => arr.map(e => e.toLowerCase());

  const containsEmail = arr => arr.some(element => emailRegex.test(element));

  const findInvalidEmail = arr => arr.find(element => !emailRegex.test(element)) || null;

  const containsPhoneNumber = arr => arr.some(element => phoneRegex.test(element));

  const findInvalidPhoneNumber = arr => arr.find(element => !phoneRegex.test(element)) || null;

  function hasNonHashedValue(arr) {
    const hexRegex = /^[0-9a-fA-F]+$/;
    return !arr.every(str => {
      return (
        (str.length === 32 ||
          str.length === 40 ||
          str.length === 64 ||
          str.length === 128) &&
        hexRegex.test(str) &&
        str !==
        str
          .split("")
          .reverse()
          .join("")
      );
    });
  }

  function isValidHashedValue(str) {
    const hexRegex = /^[0-9a-fA-F]+$/;
    const isValidLength = (str.length === 32 || str.length === 40 || str.length === 64 || str.length === 128);
    const isHexFormat = hexRegex.test(str);
    const isNotPalindrome = str !== str.split("").reverse().join("");
    return isValidLength && isHexFormat && isNotPalindrome;
  }

  const encryptStringBasedOnHashSelection = value => {
    switch (selectedHashingMethod) {
      case "SHA512":
        return encryptStringSHA512(value);
      case "SHA256":
        return encryptStringSHA256(value);
      case "SHA3":
        return encryptStringSHA3(value);
      case "SHA1":
        return encryptStringSHA1(value);
      case "MD5":
        return encryptStringMD5(value);
      default:
        return encryptStringSHA256(value);
    }
  };

  // Progress tracking function
  const updateProgress = async (stepProgress, status, dsp = null) => {
    if (!createdEmailListId) return;
    
    try {
      await updateHashedEmailList({
        id: createdEmailListId,
        stepProgress: stepProgress,
        status: status,
        ...(dsp && { dsp: dsp.toUpperCase() })
      });
    } catch (error) {
      console.error("Error updating progress:", error);
    }
  };

  const customRequest = ({ file, onError }) => {
    if (file.name.toLowerCase().includes("csv")) {
      Papa.parse(file, {
        skipEmptyLines: "greedy",
        error: function (err, file, inputElem, reason) {
          onError({ err, file, inputElem, reason });
        },
        complete: async results => {
          const currentEmails = removeDuplicates(results.data.flat(4));
          
          // Auto-detect if data is hashed before processing
          const areHashedEmails = detectHashedEmails(currentEmails);
          
          // Only convert to lowercase for emails if not hashed
          let processedEmails = currentEmails;
          if (selectedDataType === "emails" && !areHashedEmails) {
            processedEmails = convertToLowercase(currentEmails);
          }
          
          const combinedEmails = [...emails, ...processedEmails];
          const uniqueEmails = removeDuplicates(combinedEmails);
          setEmails(uniqueEmails);
          
          // Auto-detect if data is hashed and toggle the switch
          if (areHashedEmails && !emailsAlreadyHashed) {
            setEmailsAlreadyHashed(true);
            message.info(`Hashed ${selectedDataType === "emails" ? "emails" : "phone numbers"} detected - automatically enabled pre-hashed data mode`);
          }
        },
      });
    } else {
      message.error("Invalid file type. Only accepts .csv");
      return;
    }
  };

  const isNameEmpty = () => {
    const nameValue = form.getFieldValue("name");
    return !nameValue || nameValue.trim() === "";
  };

  const isNameValid = () => {
    const nameValue = form.getFieldValue("name");
    if (!nameValue || nameValue.trim() === "") return false;
    const validNameRegex = /^[a-zA-Z0-9_-]+$/;
    return validNameRegex.test(nameValue.trim());
  };

  const handleSortByName = () => {
    let sorted;
    if (sortOrder === "ascending") {
      sorted = [...emails].sort((a, b) => a.localeCompare(b));
      setSortOrder("descending");
    } else {
      sorted = [...emails].sort((a, b) => b.localeCompare(a));
      setSortOrder("ascending");
    }
    setEmails(sorted);
  };

  // Step 1: Upload Emails
  const handleUploadEmails = async () => {
    let encryptedEmails = [];

    if (isNameEmpty()) {
      return message.error("Please input list name.");
    }

    if (!isNameValid()) {
      return message.error("Name can only contain letters, numbers, dash (-) and underscore (_). No spaces or other special characters allowed.");
    }

    if (selectedDataType === "emails") {
      if (
        emailsAlreadyHashed &&
        (containsEmail(emails) || hasNonHashedValue(emails))
      ) {
        return message.warning(
          "One or more of the values in your data contains an email address. Please ensure that all values are hashed or use an email-free dataset."
        );
      }

      if (!emailsAlreadyHashed && findInvalidEmail(emails)) {
        return message.warning(
          "One or more of the values in your data is not a valid email address. Please correct your data or use an email-free dataset."
        );
      }
    } else {
      if (
        emailsAlreadyHashed &&
        (containsPhoneNumber(emails) || hasNonHashedValue(emails))
      ) {
        return message.warning(
          "One or more of the values in your data contains a phone number. Please ensure that all values are hashed or use a phone number-free dataset."
        );
      }

      if (!emailsAlreadyHashed && findInvalidPhoneNumber(emails)) {
        return message.warning(
          "One or more of the values in your data is not a valid phone number. Please correct your data or use a phone number-free dataset."
        );
      }
    }

    if (!emailsAlreadyHashed) {
      encryptedEmails = emails.map(email =>
        encryptStringBasedOnHashSelection(email)
      );
      setEmails(encryptedEmails);
    } else {
      encryptedEmails = emails;
    }

    setIsUploading(true);

    const chunkSize = 1000;

    let progressCount = 0;
    let isLastIteration = false;

    try {
      const response = await createHashedEmailList({
        name: form.getFieldValue("name"),
        stepProgress: 1,
        status: "UPLOADED",
        typeOfFirstPartyData: selectedDataType
      });

      const emailListId = response.data.createHashedEmailList.id;
      setCreatedEmailListId(emailListId);

      for (let i = 0; i < encryptedEmails.length; i += chunkSize) {
        const chunk = encryptedEmails.slice(i, i + chunkSize);
        progressCount += chunkSize;

        isLastIteration = i + chunkSize >= encryptedEmails.length;
        const response = await bulkCreateHashedEmailAddress({
          emailListId: emailListId,
          emails: chunk,
          refetch: isLastIteration,
        });

        setHashedEmailCount(
          prevCount =>
            prevCount + response.data.bulkCreateHashedEmailAddress.uniqueEmails
        );

        setProgress(Math.floor((progressCount / encryptedEmails.length) * 100));
      }

      await updateHashedEmailList({
        id: emailListId,
        tag: String(encryptedEmails.length)
      });

      setIsUploading(false);
      setUploadComplete(true);
      
      message.success("Email upload completed successfully!");
      
    } catch (error) {
      setIsUploading(false);
      message.error("Error uploading emails: " + error.message);
    }
  };

  // Step 2: Map to Device IDs
  const handleMapToDeviceIds = async () => {
    if (!createdEmailListId) {
      return message.error("No email list found to process");
    }

    try {
      setIsMappingDevices(true);
      
      const response = await activateHashedEmailList({
        orgID: currentOrg?.id,
        hashedEmailListIds: [createdEmailListId],
        dsp: selectedDspProvider.toUpperCase()
      });
      
      if (
        response &&
        response.data &&
        response.data.activateHashedEmailList &&
        response.data.activateHashedEmailList.success
      ) {
        setIsMappingDevices(false);
        setIsMappingComplete(true);
        
        message.success("Device IDs mapping completed successfully!");
      } else if (
        response &&
        response.data &&
        response.data.activateHashedEmailList &&
        response.data.activateHashedEmailList.error
      ) {
        setIsMappingDevices(false);
        message.error(response.data.activateHashedEmailList.error);
      } else {
        setIsMappingDevices(false);
        message.error(
          "No device IDs were processed. Please make sure there are emails to process."
        );
      }
    } catch (error) {
      setIsMappingDevices(false);
      message.error("Error mapping device IDs: " + error.message);
    }
  };


  const handleNext = () => {
    if (currentStep === 0 && !isUploadComplete) {
      handleUploadEmails();
    } else if (currentStep === 1 && !isMappingComplete) {
      handleMapToDeviceIds();
    } else {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleFinishAndCheckLater = () => {
    message.success("Workflow completed! You can check the status later from the main view.");
    handleClose();
  };

  const handlePrev = () => {
    setCurrentStep(currentStep - 1);
  };

  const handleClose = async () => {
    // First call onClose to close the modal immediately
    onClose();
    
    // Then reset all states after the modal is closed
    setTimeout(() => {
      setCurrentStep(0);
      form.resetFields();
      setEmails([]);
      setProgress(0);
      setHashedEmailCount(0);
      setUploadComplete(false);
      setIsMappingComplete(false);
      setEmailsAlreadyHashed(false);
      setSelectedHashingMethod("SHA256");
      setCreatedEmailListId(null);
      
      // Refetch the table to show any newly created audiences (only if an audience was actually created)
      if (refetchHashedEmailLists && createdEmailListId) {
        try {
          refetchHashedEmailLists();
        } catch (error) {
          console.error("Error refetching email lists:", error);
        }
      }
    }, 0);
  };

  const getEmailModalColumns = () => [
    {
      title: selectedDataType === "emails" ? "Email" : "Phone Number",
      dataIndex: "email",
      key: "email",
      render: text => {
        // Check if it's a valid hash first (regardless of toggle state)
        const isValidHash = isValidHashedValue(text);
        
        // Check if it's in original format (email or phone)
        const isOriginalFormat = selectedDataType === "emails" ? emailRegex.test(text) : phoneRegex.test(text);
        
        // If toggle is ON, we expect hashes, if OFF we expect original format
        // But we should always accept valid hashes OR valid original formats
        const isValid = emailsAlreadyHashed 
          ? isValidHash  // When toggle is ON, prefer hashes but accept both
          : (isOriginalFormat || isValidHash); // When toggle is OFF, prefer original but accept hashes too
        
        return (
          <span
            style={{
              color: isValid ? "inherit" : "red",
            }}
          >
            {text} {isValid ? "" : "(Invalid Value)"}
          </span>
        );
      },
    },
    {
      title: "Option",
      dataIndex: "option",
      key: "option",
      render: (_, email) => (
        <Popconfirm
          title={`Are you sure you want to delete this ${selectedDataType === "emails" ? "email" : "phone number"}?`}
          onConfirm={async e => {
            const index = emails.findIndex(element => element === email.email);
            if (index !== -1) {
              const newData = [...emails];
              newData.splice(index, 1);
              setEmails(newData);
              message.success(`${selectedDataType === "emails" ? "Email" : "Phone number"} deleted`);
            }
          }}
          okText="Yes"
          cancelText="No"
          disabled={isUploadComplete}
        >
          <DeleteOutlined style={{ color: isUploadComplete ? '#d9d9d9' : 'inherit', cursor: isUploadComplete ? 'not-allowed' : 'pointer' }} />
        </Popconfirm>
      ),
    },
  ];

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <div>
            <Title level={4}>Upload Your {selectedDataType === "emails" ? "Email" : "Phone Number"} List</Title>
            <Text style={{ display: "block", marginBottom: "24px" }}>
              Upload a CSV file containing your {selectedDataType === "emails" ? "email addresses" : "phone numbers"}. The system will process and hash them for privacy.
            </Text>

            {/* Data Type Selection */}
            <Row style={{ marginBottom: "24px" }}>
              <Col span={24}>
                <Text strong style={{ display: "block", marginBottom: "8px" }}>Select Data Type:</Text>
                <Radio.Group
                  value={selectedDataType}
                  onChange={(e) => setSelectedDataType(e.target.value)}
                  disabled={isUploading || isUploadComplete}
                >
                  <Radio value="emails">Email Addresses</Radio>
                  <Radio value="phones">Phone Numbers</Radio>
                </Radio.Group>
              </Col>
            </Row>

            <Row gutter={16} style={{ marginBottom: "24px" }}>
              <Col span={16}>
                <Form form={form} layout="vertical">
                  <Form.Item
                    label="Audience Name"
                    name="name"
                    rules={[
                      { required: true, message: "Please input your audience name." },
                      {
                        pattern: /^[a-zA-Z0-9_-]+$/,
                        message: "Name can only contain letters, numbers, dash (-) and underscore (_).",
                      },
                      {
                        max: 30,
                        message: "Audience name cannot exceed 30 characters.",
                      },
                    ]}
                  >
                    <Input
                      placeholder="Enter audience name"
                      disabled={isUploading || isUploadComplete}
                      maxLength={30}
                      showCount
                    />
                  </Form.Item>
                </Form>
              </Col>
              <Col span={8}>
                <Form.Item label=" ">
                  <TemplateCSVDownloader
                    tooltipMessage={`Download CSV template for ${selectedDataType === "emails" ? "email" : "phone number"} list`}
                    rows={selectedDataType === "emails" ? HashedEmailConfig.csvTemplate.rows : PhoneNumberConfig.csvTemplate.rows}
                    templateName={selectedDataType === "emails" ? HashedEmailConfig.csvTemplate.templateName : PhoneNumberConfig.csvTemplate.templateName}
                    title={`Sample ${selectedDataType === "emails" ? "Email" : "Phone Number"} list template`}
                    styles={{ width: "100%" }}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Popover
              content={
                <ol style={{ fontSize: "14px" }}>
                  <li>Select your data type (Email Addresses or Phone Numbers)</li>
                  <li>Enter the {selectedDataType === "emails" ? "Email" : "Phone Number"} List Name</li>
                  <li>Upload a CSV file. Remove headers from columns. Download the Sample Template for reference</li>
                  <li>Select your desired options:
                    <ol>
                      <li>If your CSV contains hashed {selectedDataType === "emails" ? "emails" : "phone numbers"}, the system will auto-detect and enable pre-hashed mode</li>
                      <li>Alternatively, select your desired hashing method</li>
                    </ol>
                  </li>
                  <li>Click on the Next button to upload</li>
                </ol>
              }
              title="Instructions"
            >
              <Button type="default" size="small" style={{ marginBottom: "16px" }}>
                Show Instructions
              </Button>
            </Popover>

            <Row gutter={16} style={{ marginBottom: "16px" }}>
              <Col span={12}>
                <Text>{selectedDataType === "emails" ? "Emails" : "Phone numbers"} are already hashed </Text>
                <Tooltip title={`Enable this option if the ${selectedDataType === "emails" ? "emails" : "phone numbers"} are already hashed. System will auto-detect.`}>
                  <Switch
                    checked={emailsAlreadyHashed}
                    onChange={(checked) => setEmailsAlreadyHashed(checked)}
                    disabled={isUploadComplete}
                  />
                </Tooltip>
                {emailsAlreadyHashed && emails.length > 0 && (
                  <div style={{ marginTop: "4px" }}>
                    <Badge status="success" text={
                      <Text style={{ fontSize: "12px", color: "#52c41a" }}>
                        Auto-detected hashed format
                      </Text>
                    } />
                  </div>
                )}
              </Col>
              <Col span={12}>
                <Text>Hashed Method Selection </Text>
                <Select
                  value={selectedHashingMethod}
                  disabled={emailsAlreadyHashed}
                  onChange={(value) => setSelectedHashingMethod(value)}
                  style={{ width: "100%" }}
                  options={[
                    { value: "SHA256", label: "SHA256" },
                  ]}
                />
              </Col>
            </Row>

            {(isUploading || isUploadComplete) && (
              <div style={{ marginBottom: "20px", textAlign: "center" }}>
                <Progress
                  percent={progress}
                  status={isUploading ? "active" : "success"}
                />
                {isUploadComplete && (
                  <Text
                    type="success"
                    style={{ color: "#52c41a", textAlign: "center", display: "block", marginTop: "8px" }}
                  >
                    Upload Complete. {`${savedHashedEmailCount}/${emails.length} ${selectedDataType === "emails" ? "emails" : "phone numbers"} were successfully saved.`}
                  </Text>
                )}
              </div>
            )}

            <Spin spinning={isUploading}>
              {emails.length === 0 ? (
                <Dragger
                  accept={".csv"}
                  showUploadList={false}
                  multiple={false}
                  customRequest={customRequest}
                  style={{ marginTop: "20px" }}
                >
                  <p className="ant-upload-drag-icon">
                    <UploadOutlined />
                  </p>
                  <p className="ant-upload-text">
                    Click or drag CSV file to this area to upload
                  </p>
                  <p className="ant-upload-hint">Import your {selectedDataType === "emails" ? "Emails" : "Phone Numbers"} here.</p>
                </Dragger>
              ) : (
                <div>
                  <Row justify="space-between" align="middle" style={{ marginBottom: "10px" }}>
                    <Col>
                      <Space>
                        <Upload
                          accept={".csv"}
                          showUploadList={false}
                          multiple={false}
                          customRequest={customRequest}
                        >
                          <Button icon={<UploadOutlined />} disabled={isUploadComplete}>
                            Add more
                          </Button>
                        </Upload>
                        <Button
                          icon={<DeleteOutlined />}
                          onClick={() => setEmails([])}
                          disabled={emails.length === 0 || isUploadComplete}
                        >
                          Clear all
                        </Button>
                        <Button
                          icon={sortIcon}
                          onClick={handleSortByName}
                          disabled={emails.length === 0}
                        >
                          Sort
                        </Button>
                      </Space>
                    </Col>
                    <Col>
                      <Text type="secondary">
                        {selectedDataType === "emails" ? "Email" : "Phone Number"} Count: {emails.length}
                      </Text>
                    </Col>
                  </Row>
                  <PaginationTable
                    columns={getEmailModalColumns()}
                    data={emails.map(email => ({ email }))}
                    total={emails.length}
                    bordered={false}
                    size="small"
                    pageSize={10}
                    style={{ height: "300px", overflowY: "auto" }}
                  />
                </div>
              )}
            </Spin>
          </div>
        );

      case 1:
        return (
          <div>
            <Title level={4}>Map to Device IDs</Title>
            <Text style={{ display: "block", marginBottom: "16px" }}>
              Converting {selectedDataType === "emails" ? "email addresses" : "phone numbers"} into device identifiers through secure hashing and identity matching. 
              This process usually takes about 30 minutes to complete.
            </Text>
            
            {/* Audience Info Display */}
            {(form.getFieldValue("name") || emails.length > 0) && (
              <Card 
                size="small" 
                style={{ 
                  marginBottom: "20px", 
                  backgroundColor: "#f8f9fa",
                  border: "1px solid #e9ecef"
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div>
                    <Text strong style={{ color: "#495057" }}>Audience: </Text>
                    <Text style={{ color: "#6c757d" }}>
                      {form.getFieldValue("name") || "Untitled Audience"}
                    </Text>
                  </div>
                  <div>
                    <Text strong style={{ color: "#495057" }}>{selectedDataType === "emails" ? "Email" : "Phone Number"} Count: </Text>
                    <Text style={{ color: "#6c757d" }}>{emails.length.toLocaleString()}</Text>
                  </div>
                </div>
              </Card>
            )}
            
            {/* DSP Selection */}
            <Form layout="vertical" style={{ marginBottom: "20px" }}>
              <Form.Item label="Select DSP Provider">
                <Text style={{ display: "block", marginBottom: "8px", fontSize: "14px", color: "#666" }}>
                  Which DSP platform would you like to activate this audience for?
                </Text>
                <Radio.Group
                  value={selectedDspProvider}
                  onChange={(e) => setSelectedDspProvider(e.target.value)}
                  disabled={isMappingDevices || isMappingComplete}
                >
                  <Radio value="liquidm" disabled={selectedDataType === "phones" || isMappingComplete}>
                    {getDSPDisplayName('LiquidM', providersVisible)} {selectedDataType === "phones" && <span style={{ color: "#8c8c8c" }}>(Not available for phone numbers)</span>}
                  </Radio>
                  <Radio value="nexxen" disabled={isMappingComplete}>
                    {getDSPDisplayName('Nexxen', providersVisible)}
                  </Radio>
                </Radio.Group>
              </Form.Item>
            </Form>
            
            <Card style={{ textAlign: "center", marginTop: "20px" }}>
              {isMappingComplete ? (
                <div>
                  <CheckCircleOutlined style={{ fontSize: "48px", color: "#52c41a", marginBottom: "16px" }} />
                  <Title level={4} style={{ color: "#52c41a" }}>Device IDs Mapping Complete!</Title>
                  <Text>Your {selectedDataType === "emails" ? "email addresses" : "phone numbers"} have been successfully converted to device identifiers. This process usually takes about 30 minutes to complete.</Text>
                </div>
              ) : (
                <div>
                  <SettingOutlined style={{ fontSize: "48px", color: "#1890ff", marginBottom: "16px" }} />
                  <Title level={4}>Ready to Map to Device IDs</Title>
                  <Text style={{ display: "block", marginBottom: "16px" }}>
                    Click "Map to Device IDs" to begin converting your {selectedDataType === "emails" ? "email addresses" : "phone numbers"} into device identifiers.
                  </Text>
                  {isMappingDevices && (
                    <div style={{ marginTop: "20px" }}>
                      <Spin size="large" />
                      <br />
                      <Text style={{ marginTop: "16px", display: "block" }}>Processing device IDs...</Text>
                    </div>
                  )}
                </div>
              )}
            </Card>
          </div>
        );


      default:
        return null;
    }
  };

  const getNextButtonText = () => {
    if (currentStep === 0) {
      return isUploadComplete ? "Next" : "Upload";
    } else if (currentStep === 1) {
      return isMappingComplete ? "Finish and Check Later" : "Map to Device IDs";
    }
    return "Next";
  };

  const getNextButtonIcon = () => {
    if (currentStep === 0) {
      return isUploadComplete ? <ArrowRightOutlined /> : <UploadOutlined />;
    } else if (currentStep === 1) {
      return isMappingComplete ? <SaveOutlined /> : <UsergroupAddOutlined />;
    }
    return <ArrowRightOutlined />;
  };

  const isNextDisabled = () => {
    if (currentStep === 0) {
      return emails.length === 0 || isUploading;
    } else if (currentStep === 1) {
      return !isUploadComplete || isMappingDevices || !selectedDspProvider;
    }
    return false;
  };

  const isComplete = false; // Never show the complete state since workflow ends after step 1

  return (
    <Modal
      title="Add New Audience"
      open={visible}
      onCancel={handleClose}
      closable={!isUploading}
      maskClosable={!isUploading}
      keyboard={!isUploading}
      width="90%"
      style={{ top: 20 }}
      bodyStyle={{ 
        maxHeight: "80vh", 
        overflowY: "hidden", 
        padding: "0"
      }}
      footer={[
        !isMappingComplete && (
          <Button key="close" onClick={handleClose} icon={<CloseOutlined />} disabled={isUploading}>
            Close
          </Button>
        ),
        !isComplete && currentStep === 0 && (
          <Button
            key="next"
            type="primary"
            onClick={handleNext}
            disabled={isNextDisabled()}
            loading={isUploading}
            icon={getNextButtonIcon()}
          >
            {getNextButtonText()}
          </Button>
        ),
        !isComplete && currentStep === 1 && !isMappingComplete && (
          <Button
            key="map"
            type="primary"
            onClick={handleNext}
            disabled={isNextDisabled()}
            loading={isMappingDevices}
            icon={getNextButtonIcon()}
          >
            {getNextButtonText()}
          </Button>
        ),
        !isComplete && currentStep === 1 && isMappingComplete && (
          <Button
            key="finish"
            type="primary"
            onClick={handleFinishAndCheckLater}
            icon={getNextButtonIcon()}
          >
            {getNextButtonText()}
          </Button>
        ),
      ]}
    >
      <Row style={{ minHeight: "80vh" }}>
        {/* Left Sidebar - Steps Navigation */}
        <Col span={6} style={{ 
          backgroundColor: "#f5f5f5", 
          padding: "24px 16px",
          borderRight: "1px solid #e8e8e8"
        }}>
          <div style={{ marginBottom: "24px" }}>
            <Title level={4} style={{ margin: "0 0 8px 0", color: "#262626" }}>Progress</Title>
            <Text type="secondary" style={{ fontSize: "12px" }}>Step {currentStep + 1} of {steps.length}</Text>
          </div>
          
          <Steps 
            current={-1}
            direction="vertical"
            size="small"
          >
            {steps.map((item, index) => {
              const isCompleted = 
                index < currentStep || 
                (index === 0 && isUploadComplete) || 
                (index === 1 && isMappingComplete);
                
              return (
                <Step 
                  key={item.title}
                  title={item.title}
                  description={item.description}
                  icon={
                    isCompleted ? (
                      <CheckCircleOutlined style={{ color: "#52c41a", fontSize: "20px" }} />
                    ) : (
                      React.cloneElement(item.icon, { 
                        style: { 
                          color: index === currentStep ? "#1890ff" : "#bfbfbf",
                          fontSize: "20px"
                        } 
                      })
                    )
                  }
                />
              );
            })}
          </Steps>
        </Col>
        
        {/* Right Content Area */}
        <Col span={18} style={{ 
          backgroundColor: "#ffffff",
          display: "flex",
          flexDirection: "column"
        }}>
          <div style={{ 
            flex: 1, 
            overflowY: "auto", 
            maxHeight: "calc(80vh - 80px)",
            padding: "24px"
          }}>
            {renderStepContent()}
          </div>
        </Col>
      </Row>
    </Modal>
  );
};

export default GuidedWorkflowSteps;
